import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Tag, Share2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-revo-bg pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-revo-paper rounded w-1/4" />
            <div className="h-12 bg-revo-paper rounded w-3/4" />
            <div className="aspect-video bg-revo-paper rounded" />
            <div className="space-y-4">
              <div className="h-4 bg-revo-paper rounded" />
              <div className="h-4 bg-revo-paper rounded w-5/6" />
              <div className="h-4 bg-revo-paper rounded w-4/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-revo-bg pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="font-barlow text-4xl font-bold text-white mb-4">
            Articolo Non Trovato
          </h1>
          <p className="font-manrope text-gray-400 mb-8">
            L'articolo che stai cercando non esiste o è stato rimosso.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-revo-orange hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna al Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-revo-bg pt-20 min-h-screen">
      {/* Header */}
      <section className="py-16 border-b border-revo-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-manrope text-sm">Torna al Blog</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-revo-orange/10 border border-revo-orange/20 text-xs font-manrope text-revo-orange uppercase tracking-wider">
                {post.category}
              </span>
              <div className="flex items-center gap-1 text-gray-500">
                <Calendar className="w-4 h-4" />
                <span className="font-manrope text-sm">{formatDate(post.created_at)}</span>
              </div>
            </div>

            <h1 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
              {post.title}
            </h1>

            <p className="font-manrope text-xl text-gray-300 leading-relaxed">
              {post.excerpt}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      {post.image_url && (
        <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-8 relative z-10">
          <div className="aspect-video overflow-hidden border border-revo-border">
            <img 
              src={post.image_url} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div 
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-barlow prose-headings:uppercase prose-headings:tracking-tight
              prose-h2:text-3xl prose-h2:text-white prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-xl prose-h3:text-white prose-h3:mt-8 prose-h3:mb-4
              prose-p:font-manrope prose-p:text-gray-400 prose-p:leading-relaxed
              prose-a:text-revo-orange prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-ul:font-manrope prose-ul:text-gray-400
              prose-li:marker:text-revo-orange"
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="blog-content"
          />
        </div>
      </section>

      {/* Share */}
      <section className="py-16 border-t border-revo-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-manrope">Altri Articoli</span>
            </Link>

            <button 
              className="inline-flex items-center gap-2 text-gray-400 hover:text-revo-orange transition-colors"
              onClick={() => navigator.share?.({ title: post.title, url: window.location.href })}
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
              <span className="font-manrope text-sm">Condividi</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
